import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled, createTheme } from '@mui/material/styles';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import BarChartIcon from '@mui/icons-material/BarChart';
import DescriptionIcon from '@mui/icons-material/Description';
import LayersIcon from '@mui/icons-material/Layers';
import { AppProvider } from '@toolpad/core/AppProvider';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import { PageContainer } from '@toolpad/core/PageContainer';
import Grid from '@mui/material/Grid';
import { BsGrid3X3GapFill } from "react-icons/bs";
import { HiUsers } from 'react-icons/hi2';
import { FaBasketShopping, FaFilePrescription } from "react-icons/fa6";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import DialogContentText from '@mui/material/DialogContentText';
import { BiSolidCoupon } from "react-icons/bi";
import DialogTitle from '@mui/material/DialogTitle';
import Modal from '@mui/material/Modal';
import { toast } from 'react-toastify';
import { FaFileAlt, FaLocationArrow } from "react-icons/fa";
import { addCoupon, deleteCoupon, getCoupons } from '../Api';

const NAVIGATION = [
    {
        kind: 'header',
        title: 'Main items',
    },
    {
        segment: 'dashboard',
        title: 'Dashboard',
        icon: <DashboardIcon />,
    },
    {
        segment: 'dashboard-products',
        title: 'Products',
        icon: <FaBasketShopping style={{ fontSize: '22px' }} />,
    },
    {
        segment: 'dashboard-users',
        title: 'Users',
        icon: <HiUsers style={{ fontSize: '22px' }} />,
    },
    {
        segment: 'dashboard-categories',
        title: 'Categories',
        icon: <BsGrid3X3GapFill style={{ fontSize: '22px' }} />,
    },
    {
        segment: 'dashboard-orders',
        title: 'Orders',
        icon: <ShoppingCartIcon />,
    },
    {
        segment: 'prescriptions',
        title: 'Prescriptions',
        icon: <FaFilePrescription style={{ fontSize: '20px' }} />,
    },
    {
        segment: 'dashboard-coupons',
        title: 'Coupons',
        icon: <BiSolidCoupon style={{ fontSize: '22px' }} />,
    },
    {
        segment: 'dashboard-content',
        title: 'Content',
        icon: <FaFileAlt style={{ fontSize: '20px' }} />,
    },
    {
        segment: 'dashboard-location',
        title: 'Location',
        icon: <FaLocationArrow />,
    },
    {
        kind: 'divider',
    },
    {
        kind: 'header',
        title: 'Analytics',
    },
    {
        segment: 'reports',
        title: 'Reports',
        icon: <BarChartIcon />,
        children: [
            {
                segment: 'sales',
                title: 'Sales',
                icon: <DescriptionIcon />,
            },
            {
                segment: 'traffic',
                title: 'Traffic',
                icon: <DescriptionIcon />,
            },
        ],
    },
    {
        segment: 'integrations',
        title: 'Integrations',
        icon: <LayersIcon />,
    },
];

const Skeleton = styled('div')(({ theme, height }) => ({
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    height,
    content: '" "',
}));

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    height: 400,
    overflowY: "scroll",
    bgcolor: "background.paper",
    border: "1px solid transparent",
    borderRadius: 1,
    shadow: 24,
    p: 4,
};

function useDemoRouter(initialPath) {
    const [pathname, setPathname] = React.useState(initialPath);
    const navigate = useNavigate();

    const router = React.useMemo(() => {
        return {
            pathname,
            searchParams: new URLSearchParams(),
            navigate: (path) => {
                setPathname(String(path));
                navigate(path);
            },
        };
    }, [pathname, navigate]);

    return router;
}

const theme = createTheme({
    palette: {
        mode: 'light',
    },
});

function DshCoupons({ onLogout }) {

    const navigate = useNavigate();
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(false);
    const router = useDemoRouter('/dashboard-coupons');
    const [open, setOpen] = React.useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
    const [couponData, setCouponData] = useState({
        code: "",
        amount: "",
        discount_type: "fixed_cart",
        description: "",
        date_expires: "",
        individual_use: false,
        product_ids: [],
        excluded_product_ids: [],
        usage_limit: "",
        usage_limit_per_user: "",
        limit_usage_to_x_items: "",
        free_shipping: false,
        product_categories: [],
        excluded_product_categories: [],
        exclude_sale_items: false,
        minimum_amount: "",
        maximum_amount: "",
        email_restrictions: []
    });

    // BUG-10: create and delete both ended in window.location.reload(), which
    // threw away the whole SPA to refresh one list — and in the delete case fired
    // before the request had finished, so the reload could abort the very DELETE
    // it was meant to reflect. Hoisted out of the effect so both handlers can
    // refetch the list properly instead.
    const loadCoupons = useCallback(async () => {
        setLoading(true)
        try {
            const response = await getCoupons();
            setCoupons(response.data || []);
        } catch (error) {
            console.log(error);
            toast.error('Failed to load coupons.');
        } finally {
            setLoading(false)
        }
    }, []);

    useEffect(() => {
        loadCoupons();
    }, [loadCoupons]);

    const handleDeleteCoupon = async (id) => {
        if (id) {
            if (window.confirm("Are you sure want to delete?")) {
                setLoading(true)
                try {
                    // `deleteCoupon(id)` was called without await, so the success
                    // toast fired regardless of outcome and the try/catch could
                    // never see a rejection. Awaiting it means a failure is
                    // actually reported instead of being announced as a success.
                    await deleteCoupon(id)
                    toast.success('Coupon successfully deleted')
                    await loadCoupons()
                } catch (error) {
                    console.log(error)
                    toast.error('Failed to delete the coupon. It has not been removed.')
                } finally{
                    setLoading(false)
                }
            }
        } else {
            toast.error('Id not found!')
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCouponData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);

            // sanitize payload
            const payload = { ...couponData };

            // Remove fields if empty
            Object.keys(payload).forEach((key) => {
                if (
                    payload[key] === "" ||
                    (Array.isArray(payload[key]) && payload[key].length === 0)
                ) {
                    delete payload[key];
                }
            });

            // Convert numbers properly
            ["usage_limit", "usage_limit_per_user", "limit_usage_to_x_items"].forEach((field) => {
                if (payload[field] !== undefined) {
                    payload[field] = parseInt(payload[field], 10);
                    if (isNaN(payload[field])) delete payload[field];
                }
            });

            await addCoupon(payload);
            toast.success("Coupon added successfully!");
            setOpen(false);
            await loadCoupons();

            // Reset form. This was unreachable before: window.location.reload()
            // sat immediately above it, so the form state was never actually
            // cleared — the page just happened to be destroyed instead.
            setCouponData({
                code: "",
                amount: "",
                discount_type: "fixed_cart",
                description: "",
                date_expires: "",
                individual_use: false,
                product_ids: [],
                excluded_product_ids: [],
                usage_limit: "",
                usage_limit_per_user: "",
                limit_usage_to_x_items: "",
                free_shipping: false,
                product_categories: [],
                excluded_product_categories: [],
                exclude_sale_items: false,
                minimum_amount: "",
                maximum_amount: "",
                email_restrictions: []
            });
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Failed to add coupon");
        } finally {
            setLoading(false);
        }
    };

    const handleLogoutClick = () => {
        setOpenLogoutDialog(true);
    };

    const handleLogoutConfirm = () => {
        setOpenLogoutDialog(false);
        onLogout();
        navigate('/');
    };

    const handleLogoutCancel = () => {
        setOpenLogoutDialog(false);
    };

    const branding = {
        logo: (
            <div className="header-logo">
                <img onClick={() => navigate('/')} src={require('../images/logo.png')} alt="" style={{ height: '52px', width: '60px' }} />
            </div>
        ),
        title: '',
    };

    const ToolbarActions = () => (
        <Button
            variant="outlined"
            startIcon={<LogoutIcon />}
            onClick={handleLogoutClick}
            sx={{ marginRight: 2 }}
            style={{ background: "#0165AB", color: "#fff" }}
        >
            Logout
        </Button>
    );

    return (
        <>
            <AppProvider
                navigation={NAVIGATION}
                router={router}
                theme={theme}
                branding={branding}
            >
                <DashboardLayout
                    slots={{
                        toolbarActions: ToolbarActions,
                    }}
                >
                    <PageContainer>
                        {loading ? (
                            <Grid container spacing={1}>
                                <Grid size={5} />
                                <Grid size={10}>
                                    <Skeleton height={35} />
                                </Grid>
                                <Grid size={2}>
                                    <Skeleton height={35} />
                                </Grid>
                                <Grid size={10}></Grid>
                                <Grid size={2}></Grid>
                                {[...Array(8)].map((_, index) => (
                                    <Grid size={3} key={index}>
                                        <Skeleton height={220} />
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <section className="dashboard-coupons">
                                <Grid container spacing={1}>
                                    <Grid size={10}></Grid>
                                    <Grid size={2}>
                                        <button
                                            style={{
                                                height: "35px",
                                                width: "100%",
                                                border: "1px solid transparent",
                                                background: "#0165AB",
                                                color: "#fff",
                                                borderRadius: "5px",
                                                cursor: "pointer",
                                            }}
                                            onClick={() => setOpen(true)}
                                        >
                                            ADD COUPON
                                        </button>

                                        <Modal open={open} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                                            <Box sx={style}>
                                                <Typography id="modal-modal-title" variant="h6" component="h2">
                                                    Add Coupon
                                                </Typography>
                                                <form onSubmit={handleSubmit} style={{ marginTop: "15px" }}>
                                                    <div className="product-input-group">
                                                        <label>Code</label>
                                                        <input
                                                            type="text"
                                                            name="code"
                                                            value={couponData.code}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                    </div>

                                                    <div className="product-input-group">
                                                        <label>Amount</label>
                                                        <input
                                                            type="number"
                                                            name="amount"
                                                            value={couponData.amount}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                    </div>

                                                    <div className="product-input-group">
                                                        <label>Discount Type</label>
                                                        <select
                                                            name="discount_type"
                                                            value={couponData.discount_type}
                                                            onChange={handleChange}
                                                        >
                                                            <option value="fixed_cart">Fixed Cart</option>
                                                            <option value="percent">Percent</option>
                                                            <option value="fixed_product">Fixed Product</option>
                                                        </select>
                                                    </div>

                                                    {couponData.discount_type === "fixed_product" && (
                                                        <>
                                                            <div className="product-input-group">
                                                                <label>Product IDs (comma separated)</label>
                                                                <input
                                                                    type="text"
                                                                    name="product_ids"
                                                                    value={couponData.product_ids}
                                                                    onChange={(e) =>
                                                                        setCouponData((prev) => ({
                                                                            ...prev,
                                                                            product_ids: e.target.value.split(",").map((id) => id.trim())
                                                                        }))
                                                                    }
                                                                    placeholder="e.g. 12, 15, 22"
                                                                />
                                                            </div>

                                                            <div className="product-input-group">
                                                                <label>Excluded Product IDs (comma separated)</label>
                                                                <input
                                                                    type="text"
                                                                    name="excluded_product_ids"
                                                                    value={couponData.excluded_product_ids}
                                                                    onChange={(e) =>
                                                                        setCouponData((prev) => ({
                                                                            ...prev,
                                                                            excluded_product_ids: e.target.value.split(",").map((id) => id.trim())
                                                                        }))
                                                                    }
                                                                    placeholder="e.g. 5, 9"
                                                                />
                                                            </div>

                                                            <div className="product-input-group">
                                                                <label>Product Categories (IDs, comma separated)</label>
                                                                <input
                                                                    type="text"
                                                                    name="product_categories"
                                                                    value={couponData.product_categories}
                                                                    onChange={(e) =>
                                                                        setCouponData((prev) => ({
                                                                            ...prev,
                                                                            product_categories: e.target.value.split(",").map((id) => id.trim())
                                                                        }))
                                                                    }
                                                                    placeholder="e.g. 3, 7"
                                                                />
                                                            </div>

                                                            <div className="product-input-group">
                                                                <label>Excluded Product Categories (IDs, comma separated)</label>
                                                                <input
                                                                    type="text"
                                                                    name="excluded_product_categories"
                                                                    value={couponData.excluded_product_categories}
                                                                    onChange={(e) =>
                                                                        setCouponData((prev) => ({
                                                                            ...prev,
                                                                            excluded_product_categories: e.target.value.split(",").map((id) => id.trim())
                                                                        }))
                                                                    }
                                                                    placeholder="e.g. 8, 10"
                                                                />
                                                            </div>
                                                        </>
                                                    )}

                                                    <div className="product-input-group">
                                                        <label>Description</label>
                                                        <textarea
                                                            name="description"
                                                            value={couponData.description}
                                                            onChange={handleChange}
                                                        ></textarea>
                                                    </div>

                                                    <div className="product-input-group">
                                                        <label>Expiry Date</label>
                                                        <input
                                                            type="date"
                                                            name="date_expires"
                                                            value={couponData.date_expires}
                                                            onChange={handleChange}
                                                        />
                                                    </div>

                                                    <div className="product-input-group d-flex">
                                                        <label style={{ width: '100%' }}>
                                                            Individual Use Only
                                                        </label>
                                                        <input type="checkbox" name="individual_use" checked={couponData.individual_use} onChange={handleChange} />
                                                    </div>

                                                    <div className="product-input-group d-flex">
                                                        <label style={{ width: '100%' }}>
                                                            Free Shipping
                                                        </label>
                                                        <input type="checkbox" name="free_shipping" checked={couponData.free_shipping} onChange={handleChange} />
                                                    </div>

                                                    <div className="product-input-group d-flex">
                                                        <label style={{ width: '100%' }}>
                                                            Exclude Sale Items
                                                        </label>
                                                        <input type="checkbox" name="exclude_sale_items" checked={couponData.exclude_sale_items} onChange={handleChange} />
                                                    </div>

                                                    <div className="product-input-group">
                                                        <label>Usage Limit Per User</label>
                                                        <input
                                                            type="number"
                                                            name="usage_limit_per_user"
                                                            value={couponData.usage_limit_per_user}
                                                            onChange={handleChange}
                                                            placeholder="e.g. 1"
                                                            min="0"
                                                        />
                                                    </div>

                                                    <div className="product-input-group">
                                                        <label>Limit Usage To X Items</label>
                                                        <input
                                                            type="number"
                                                            name="limit_usage_to_x_items"
                                                            value={couponData.limit_usage_to_x_items}
                                                            onChange={handleChange}
                                                            placeholder="e.g. 5"
                                                            min="0"
                                                        />
                                                    </div>

                                                    <div className="product-input-group">
                                                        <label>Minimum Amount</label>
                                                        <input
                                                            type="number"
                                                            name="minimum_amount"
                                                            value={couponData.minimum_amount}
                                                            onChange={handleChange}
                                                        />
                                                    </div>

                                                    <div className="product-input-group">
                                                        <label>Maximum Amount</label>
                                                        <input
                                                            type="number"
                                                            name="maximum_amount"
                                                            value={couponData.maximum_amount}
                                                            onChange={handleChange}
                                                        />
                                                    </div>

                                                    <div style={{ marginTop: "15px", display: "flex", justifyContent: "space-between" }}>
                                                        <button
                                                            type="button"
                                                            onClick={handleClose}
                                                            style={{ background: "red", color: "#fff", border: "none", borderRadius: "5px", padding: "8px 20px", cursor: 'pointer' }}
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            type="submit"
                                                            disabled={loading}
                                                            style={{ background: "#0165AB", color: "#fff", border: "none", borderRadius: "5px", padding: "8px 20px", cursor: 'pointer' }}
                                                        >
                                                            {loading ? "Adding..." : "Add"}
                                                        </button>
                                                    </div>
                                                </form>
                                            </Box>
                                        </Modal>
                                    </Grid>
                                    <Grid size={12}>
                                        <div style={{ marginTop: "20px" }}>
                                            {coupons.length === 0 ? (
                                                <p>No coupons found.</p>
                                            ) : (
                                                <table
                                                    style={{
                                                        width: "100%",
                                                        borderCollapse: "collapse",
                                                        marginTop: "15px",
                                                    }}
                                                >
                                                    <thead>
                                                        <tr style={{ background: "#f0f0f0" }}>
                                                            <th style={{ padding: "10px", border: "1px solid #ddd" }}>Code</th>
                                                            <th style={{ padding: "10px", border: "1px solid #ddd" }}>Amount</th>
                                                            <th style={{ padding: "10px", border: "1px solid #ddd" }}>Type</th>
                                                            <th style={{ padding: "10px", border: "1px solid #ddd" }}>Usage</th>
                                                            <th style={{ padding: "10px", border: "1px solid #ddd" }}>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {coupons.map((coupon) => (
                                                            <tr key={coupon.id} style={{ textAlign: 'center' }}>
                                                                <td style={{ padding: "10px", border: "1px solid #ddd" }}>{coupon.code}</td>
                                                                <td style={{ padding: "10px", border: "1px solid #ddd" }}>{coupon.amount}</td>
                                                                <td style={{ padding: "10px", border: "1px solid #ddd" }}>{coupon.discount_type}</td>
                                                                <td style={{ padding: "10px", border: "1px solid #ddd" }}>{coupon.usage_count}</td>
                                                                <td style={{ padding: "10px", border: "1px solid #ddd" }}>
                                                                    <button
                                                                        onClick={() => handleDeleteCoupon(coupon.id)}
                                                                        style={{
                                                                            background: "red",
                                                                            color: "#fff",
                                                                            border: "none",
                                                                            display: 'block',
                                                                            margin: 'auto',
                                                                            borderRadius: "5px",
                                                                            padding: "5px 10px",
                                                                            cursor: "pointer",
                                                                        }}
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    </Grid>
                                </Grid>
                            </section>
                        )}
                    </PageContainer>
                </DashboardLayout>
                <Dialog
                    open={openLogoutDialog}
                    onClose={handleLogoutCancel}
                    aria-labelledby="logout-dialog-title"
                    aria-describedby="logout-dialog-description"
                >
                    <DialogTitle id="logout-dialog-title">Confirm Logout</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="logout-dialog-description">
                            Are you sure you want to log out?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleLogoutCancel} disabled={loading}>Cancel</Button>
                        <Button onClick={handleLogoutConfirm} autoFocus disabled={loading}>
                            Logout
                        </Button>
                    </DialogActions>
                </Dialog>
            </AppProvider>
        </>
    )
}

export default DshCoupons